import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText, Mail, Search, Loader2, Send, CheckCircle2, Clock, XCircle, RotateCcw, Plane, AlertCircle, Building2, CreditCard, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";
import TripEmailComposer from "@/pages/trips/TripEmailComposer";
import { ConnectEmailDialog } from "@/pages/trips/AsistenteTab";

const ALL = "__all__";
const DEFAULT_PAGO = "pagada_sin_factura";
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("es-ES") : "—");

// Matriz pago × factura (la clave operativa: "Pagada · SIN factura")
const PAGOS = {
  pagada_sin_factura: { label: "Pagada · sin factura", short: "Pagada · sin factura", icon: AlertCircle, cls: "border-rose-200 bg-rose-50 text-rose-700", tile: "bg-rose-100 text-rose-700" },
  pagada_con_factura: { label: "Pagada · con factura", short: "Pagada · con factura", icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-700", tile: "bg-emerald-100 text-emerald-700" },
  no_pagada_sin_factura: { label: "No pagada · sin factura", short: "No pagada · sin factura", icon: Clock, cls: "border-amber-200 bg-amber-50 text-amber-700", tile: "bg-amber-100 text-amber-700" },
  no_pagada_con_factura: { label: "No pagada · con factura", short: "No pagada · con factura", icon: FileText, cls: "border-sky-200 bg-sky-50 text-sky-700", tile: "bg-sky-100 text-sky-700" },
};
const PAGO_ORDER = ["pagada_sin_factura", "pagada_con_factura", "no_pagada_sin_factura", "no_pagada_con_factura"];

// Según solicitud (independiente del pago)
const SOLICITUD = { no_solicitada: "No solicitada", solicitada: "Solicitada", recibida: "Recibida", no_aplica: "No aplica" };
const SOLICITUD_ORDER = ["no_solicitada", "solicitada", "recibida", "no_aplica"];

function PagoBadge({ pagada }) {
  return pagada
    ? <Badge variant="outline" className="gap-1 text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700"><Wallet className="h-3 w-3" />Pagada</Badge>
    : <Badge variant="outline" className="gap-1 text-[10px] border-zinc-200 bg-zinc-50 text-zinc-600"><CreditCard className="h-3 w-3" />No pagada</Badge>;
}

function FacturaBadge({ r }) {
  if (r.estado_solicitud === "no_aplica") return <Badge variant="outline" className="gap-1 text-[10px] border-zinc-200 bg-zinc-50 text-zinc-400"><XCircle className="h-3 w-3" />No aplica</Badge>;
  if (r.recibida) return <Badge variant="outline" className="gap-1 text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-3 w-3" />Recibida</Badge>;
  if (r.solicitada) return <Badge variant="outline" className="gap-1 text-[10px] border-amber-200 bg-amber-50 text-amber-700"><Clock className="h-3 w-3" />Solicitada · sin recibir</Badge>;
  return <Badge variant="outline" className="gap-1 text-[10px] border-zinc-200 bg-zinc-50 text-zinc-600"><XCircle className="h-3 w-3" />Sin factura</Badge>;
}

function DateDialog({ open, onClose, row, onConfirm, pending }) {
  const [fecha, setFecha] = useState(todayISO());
  React.useEffect(() => { if (open) setFecha(row?.solicitada_at?.slice(0, 10) || todayISO()); }, [open, row]);
  if (!row) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sf-date-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />Registrar solicitud de factura</DialogTitle>
          <DialogDescription className="text-xs">{row.service} · {row.provider_name || "—"}. Indica la fecha en que se solicita; se guarda quién la solicita.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label className="text-xs">Fecha de solicitud *</Label>
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="sf-date-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onConfirm(fecha)} disabled={!fecha || pending} data-testid="sf-date-confirm">
            {pending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4" />}Marcar solicitada
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SolicitudFacturaTab() {
  const qc = useQueryClient();
  const [tripId, setTripId] = useState(ALL);
  const [solicitud, setSolicitud] = useState(ALL);
  const [pago, setPago] = useState(DEFAULT_PAGO);
  const [q, setQ] = useState("");
  const [composer, setComposer] = useState(null);
  const [showConnect, setShowConnect] = useState(false);
  const [dateRow, setDateRow] = useState(null);

  const tripsQ = useQuery({ queryKey: ["sf-trips"], queryFn: async () => (await api.get("/solicitud-factura/trips")).data });
  const trips = tripsQ.data?.items || [];

  const params = useMemo(() => ({
    trip_id: tripId === ALL ? undefined : tripId,
    solicitud: solicitud === ALL ? undefined : solicitud,
    pago: pago === ALL ? undefined : pago,
    q: q.trim() || undefined,
  }), [tripId, solicitud, pago, q]);
  const listQ = useQuery({ queryKey: ["sf-list", params], queryFn: async () => (await api.get("/solicitud-factura/list", { params })).data });
  const rows = listQ.data?.reservas || [];
  const pagoSummary = listQ.data?.pago_summary || {};
  const solicitudSummary = listQ.data?.solicitud_summary || {};
  const emailConnected = !!listQ.data?.email_connected;
  const senderEmail = listQ.data?.sender_email;

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["sf-list"] }); qc.invalidateQueries({ queryKey: ["sf-trips"] }); qc.invalidateQueries({ queryKey: ["por-estado"] }); };

  const solicitarMut = useMutation({
    mutationFn: async ({ rid, fecha }) => (await api.post(`/solicitud-factura/reserva/${rid}/solicitar`, { fecha })).data,
    onSuccess: () => { toast.success("Factura solicitada"); invalidate(); setDateRow(null); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const recibidaMut = useMutation({
    mutationFn: async ({ rid, recibida }) => (await api.post(`/solicitud-factura/reserva/${rid}/recibida`, { recibida })).data,
    onSuccess: (_, v) => { toast.success(v.recibida ? "Marcada como recibida" : "Marcada como pendiente"); invalidate(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const cancelarMut = useMutation({
    mutationFn: async (rid) => (await api.post(`/solicitud-factura/reserva/${rid}/cancelar`)).data,
    onSuccess: () => { toast.success("Solicitud anulada"); invalidate(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const onCloseComposer = (refresh) => { setComposer(null); if (refresh) invalidate(); };

  return (
    <div className="space-y-4" data-testid="solicitud-factura-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Solicita a los proveedores la factura de cada reserva. Por defecto verás las <strong>pagadas sin factura</strong> (las que hay que reclamar).</p>
        {emailConnected ? (
          <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-1.5 text-sm" data-testid="sf-email-connected">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> Enviando como <span className="font-medium">{senderEmail}</span>
            <Button variant="ghost" size="sm" className="ml-1 h-7" onClick={() => setShowConnect(true)}>Cambiar</Button>
          </div>
        ) : (
          <Button onClick={() => setShowConnect(true)} data-testid="sf-connect-email"><Mail className="mr-1.5 h-4 w-4" />Conectar mi correo</Button>
        )}
      </div>

      {/* Matriz pago × factura (clicable) */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {PAGO_ORDER.map((k) => {
          const meta = PAGOS[k];
          const Icon = meta.icon;
          const active = pago === k;
          const isKey = k === DEFAULT_PAGO;
          return (
            <button key={k} type="button" onClick={() => setPago(active ? ALL : k)}
              className={`flex items-center gap-3 rounded-xl border bg-card px-4 py-3 text-left transition-all hover:shadow-md ${active ? "border-primary ring-2 ring-primary/30" : isKey ? "border-rose-200" : ""}`}
              data-testid={`sf-tile-${k}`}>
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${meta.tile}`}><Icon className="h-4 w-4" /></div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase leading-tight tracking-wide text-muted-foreground">{meta.short}</p>
                <p className="text-xl font-bold leading-none tabular-nums">{pagoSummary[k] ?? 0}</p>
              </div>
            </button>
          );
        })}
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><Plane className="h-3 w-3" />Viaje</Label>
            <Select value={tripId} onValueChange={setTripId}>
              <SelectTrigger className="h-9 w-[280px]" data-testid="sf-filter-trip"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos los viajes</SelectItem>
                {trips.map((t) => (
                  <SelectItem key={t.trip_id} value={t.trip_id}>{t.client_name} · {t.destination || "—"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Según solicitud</Label>
            <Select value={solicitud} onValueChange={setSolicitud}>
              <SelectTrigger className="h-9 w-[180px]" data-testid="sf-filter-solicitud"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas</SelectItem>
                {SOLICITUD_ORDER.map((k) => (
                  <SelectItem key={k} value={k}>{SOLICITUD[k]}{solicitudSummary[k] != null ? ` (${solicitudSummary[k]})` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Según pago</Label>
            <Select value={pago} onValueChange={setPago}>
              <SelectTrigger className="h-9 w-[220px]" data-testid="sf-filter-pago"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas</SelectItem>
                {PAGO_ORDER.map((k) => <SelectItem key={k} value={k}>{PAGOS[k].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="relative min-w-[180px] flex-1 space-y-1">
            <Label className="text-xs">Buscar</Label>
            <Search className="absolute left-2.5 top-[30px] h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cliente, servicio, proveedor…" className="h-9 pl-8" data-testid="sf-search" />
          </div>
        </CardContent>
      </Card>

      {!emailConnected && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4" /> Conecta tu correo para poder enviar solicitudes de factura por email.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="sf-summary">
        <Badge variant="secondary">{rows.length} reserva(s)</Badge>
        {pago !== ALL && <span className="text-xs text-muted-foreground">· filtro: <strong>{PAGOS[pago]?.label}</strong></span>}
      </div>

      {listQ.isLoading ? <Skeleton className="h-64 w-full" /> : rows.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="sf-empty">No hay reservas con los filtros aplicados.</CardContent></Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                {tripId === ALL && <th className="px-3 py-3">Cliente</th>}
                <th className="px-3 py-3">Servicio</th>
                <th className="px-3 py-3">Proveedor</th>
                <th className="px-3 py-3">Pago</th>
                <th className="px-3 py-3">Factura</th>
                <th className="px-3 py-3">Solicitada</th>
                <th className="px-3 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t align-middle hover:bg-zinc-50/50" data-testid={`sf-row-${r.id}`}>
                  {tripId === ALL && <td className="px-3 py-2.5"><div className="font-medium">{r.client_name}</div><div className="text-[11px] text-muted-foreground">{r.agent}</div></td>}
                  <td className="px-3 py-2.5">
                    <div className="font-medium">{r.service}</div>
                    <div className="text-[11px] text-muted-foreground">{[fmtD(r.date_entry), r.city].filter(Boolean).join(" · ")}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5"><Building2 className="h-3 w-3 text-muted-foreground" />{r.provider_name || "—"}</div>
                    {r.provider_email && <div className="text-[11px] text-muted-foreground">{r.provider_email}</div>}
                  </td>
                  <td className="px-3 py-2.5" data-testid={`sf-pago-${r.id}`}><PagoBadge pagada={r.pagada} /></td>
                  <td className="px-3 py-2.5" data-testid={`sf-factura-${r.id}`}><FacturaBadge r={r} /></td>
                  <td className="px-3 py-2.5 text-[11px] text-muted-foreground">
                    {r.solicitada ? <>{fmtD(r.solicitada_at)}<br />{r.solicitada_by}</> : "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      {r.estado_solicitud === "no_aplica" ? (
                        <span className="text-[11px] text-muted-foreground">—</span>
                      ) : (
                        <>
                          {r.mail_ready && !r.recibida && (
                            <Button size="sm" variant="outline" className="h-8" onClick={() => setComposer({ tripId: r.trip_id, providerId: r.provider_id, reservaIds: [r.id], toEmail: r.provider_email, providerName: r.provider_name })} data-testid={`sf-email-${r.id}`}>
                              <Send className="mr-1 h-3.5 w-3.5" />Por email
                            </Button>
                          )}
                          {!r.solicitada && !r.recibida && (
                            <Button size="sm" variant="outline" className="h-8" onClick={() => setDateRow(r)} data-testid={`sf-solicitar-${r.id}`}>
                              <FileText className="mr-1 h-3.5 w-3.5" />Solicitar
                            </Button>
                          )}
                          {!r.recibida && (
                            <Button size="sm" variant="outline" className="h-8 text-emerald-700" onClick={() => recibidaMut.mutate({ rid: r.id, recibida: true })} data-testid={`sf-recibida-${r.id}`}>
                              <CheckCircle2 className="mr-1 h-3.5 w-3.5" />Recibida
                            </Button>
                          )}
                          {r.solicitada && (
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" title="Anular solicitud" onClick={() => cancelarMut.mutate(r.id)} data-testid={`sf-cancelar-${r.id}`}>
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DateDialog open={!!dateRow} onClose={() => setDateRow(null)} row={dateRow}
        pending={solicitarMut.isPending}
        onConfirm={(fecha) => solicitarMut.mutate({ rid: dateRow.id, fecha })} />

      {composer && (
        <TripEmailComposer open={!!composer} onClose={onCloseComposer} tripId={composer.tripId}
          providerId={composer.providerId} reservaIds={composer.reservaIds} toEmail={composer.toEmail}
          providerName={composer.providerName} multi={false} emailConnected={emailConnected}
          senderEmail={senderEmail} apiBase="/solicitud-factura" titleSingle="Solicitar factura al proveedor" />
      )}

      <ConnectEmailDialog open={showConnect} onClose={() => setShowConnect(false)} currentEmail={senderEmail}
        onConnected={() => qc.invalidateQueries({ queryKey: ["sf-list"] })} />
    </div>
  );
}
